/*
  * Copyright Mission 42 Ventures, Inc. All rights reserved.
  * Unauthorized copying, distribution, or use is prohibited.
  */

import React, { useMemo, useState } from 'react';
import VisibilityOutlinedIcon from '@material-ui/icons/VisibilityOutlined';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import { Entity } from '@backstage/catalog-model';
import { SidebarItem } from '@backstage/core-components';
import { useViewAs } from '../viewAs/ViewAsContext';
import { ViewAsPicker } from '../viewAs/ViewAsPicker';
import { TeamAvatar } from '../home/TeamAvatar';

const pictureOf = (entity: Entity | undefined): string | undefined =>
  (entity?.spec?.profile as { picture?: string } | undefined)?.picture;

/**
 * Sidebar entry for the app-wide "View as" lens, placed with the account
 * controls at the bottom (it's an identity switch). Visible only to users the
 * backend gate allows. While active it shows the viewed user's avatar (or a
 * lock for the read-only identity) — so you recognize whose view you're in even
 * when the name truncates — and opens the picker.
 */
export const ViewAsSidebarItem = () => {
  const {
    canViewAs,
    viewingAs,
    isReadOnly,
    viewAsRef,
    viewAsEntity,
    viewAsLogin,
    viewAsLabel,
    setViewAs,
  } = useViewAs();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  // Icon: the viewed user's avatar while active; a lock for read-only; the
  // outlined eye when idle. Memoized so SidebarItem doesn't remount it.
  const icon = useMemo(() => {
    if (!viewingAs) return VisibilityOutlinedIcon;
    if (isReadOnly) return LockOutlinedIcon;
    const slug = viewAsEntity?.metadata.name ?? viewAsLogin ?? 'user';
    const picture = pictureOf(viewAsEntity);
    const AvatarIcon = () => (
      <TeamAvatar slug={slug} pictureUrl={picture} size={20} />
    );
    return AvatarIcon;
  }, [viewingAs, isReadOnly, viewAsEntity, viewAsLogin]);

  if (!canViewAs) return null;

  return (
    <>
      <SidebarItem
        icon={icon}
        text={viewingAs ? viewAsLabel ?? 'Viewing' : 'View as'}
        onClick={(e: React.MouseEvent<Element>) =>
          setAnchorEl(e.currentTarget as HTMLElement)
        }
      />
      <ViewAsPicker
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        activeRef={viewAsRef}
        onPick={ref => setViewAs(ref)}
        onClose={() => setAnchorEl(null)}
      />
    </>
  );
};
